import React from "react";
import { AppContext } from "../store/genesys.context";
import type { ChatControls } from "./ChatbotConfig";

/**
 * Renders nothing — it only publishes the imperative chat controls to the host
 * page. It has to live *inside* AppContextProvider (that is where the open/close
 * state lives), so the controls cannot exist before React has committed. Until
 * they do, mountChatbot queues calls (see its `runOrQueue`).
 */
const ChatControlsBridge = ({
  onControlsReady,
}: {
  onControlsReady: (controls: ChatControls | null) => void;
}): null => {
  const {
    setChatOpen,
  }: {
    setChatOpen: (state: boolean) => void;
  } = React.useContext(AppContext);

  // Kept in a ref so a changed callback identity never re-runs the registration
  // effect (which would deregister and re-register the controls).
  const onControlsReadyRef = React.useRef(onControlsReady);
  React.useEffect(() => {
    onControlsReadyRef.current = onControlsReady;
  });

  React.useEffect(() => {
    onControlsReadyRef.current({
      open: () => setChatOpen(true),
      close: () => setChatOpen(false),
    });

    return () => onControlsReadyRef.current(null);
  }, [setChatOpen]);

  return null;
};

export default ChatControlsBridge;
